import { Box, Heading, Text, VStack } from '@chakra-ui/react'
import { faVideo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type ReactElement, useEffect } from 'react'

import logger from '@/utils/logger'

import { getBreakoutSessionJoinUrl } from '@/client/services/url'

import Alert from '@/components/UI/Alert'
import TrackEventButton from '@/components/UI/TrackEventButton'
import useRealtimeJoinBreakoutSession from '@/components/Worlds/hooks/session/useRealtimeJoinBreakoutSession'
import Card from '@/components/Worlds/UI/Card'

type BannerState = 'waiting' | 'assigned'

const STATE_TO_TEXT: Record<BannerState, { title: string; description: string }> = {
  waiting: {
    title: 'Waiting for breakouts to be assigned',
    description: 'The tutor will assign you to a breakout room shortly',
  },
  assigned: {
    title: 'You have been assigned to a breakout room',
    description:
      'Join the room now! Please wait for your partner in the room for 3 minutes before returning to the main Zoom room to be reassigned.',
  },
}

export default function BreakoutSessionJoinBanner({
  sessionId,
}: {
  sessionId: number
}): ReactElement {
  const { registration, error } = useRealtimeJoinBreakoutSession(sessionId)
  const breakoutSessionId = registration?.session_breakout_id

  const state = breakoutSessionId != null ? 'assigned' : 'waiting'
  const joinUrl =
    breakoutSessionId != null ? getBreakoutSessionJoinUrl(breakoutSessionId) : undefined

  useEffect(() => {
    if (error != null) {
      logger.error('Error loading registrations', { error })
    }
  }, [error])

  return (
    <Card width="100%" mb={4}>
      {error != null ? (
        <Alert variant="error">
          There was an error loading your breakout session information. Please try again later.
        </Alert>
      ) : (
        <VStack spacing={6} align="center" py={4}>
          <Box bg="primary.subtle" p={4} borderRadius="xl" color="primary.fg">
            <FontAwesomeIcon icon={faVideo} size="2x" />
          </Box>

          <VStack spacing="4px">
            <Heading textAlign="center" size="lg">
              {STATE_TO_TEXT[state].title}
            </Heading>
            <Text textAlign="center" color="fg.muted" fontSize="md">
              {STATE_TO_TEXT[state].description}
            </Text>
          </VStack>

          <TrackEventButton
            as="a"
            href={joinUrl}
            rel="noreferrer"
            target="_blank"
            isDisabled={joinUrl == null}
            trackEvent={{ action: 'Breakout Session Join' }}
          >
            Join
          </TrackEventButton>
        </VStack>
      )}
    </Card>
  )
}
